import { Cancel, CloudUpload } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
// import { ReactGrid } from '@silevis/reactgrid';
import ReactGrid from "../ReactGridComponent";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import RTFOUploadDataChart from './RTFOUploadDataChart';
import { useTheme } from '@mui/styles';
import { formatNumber } from '../../helpers/formatNumber';
import { useSnackbar } from 'notistack';
import { useSocket } from '../../context/useSocket';
import { rtfoConversionFactors } from './rtfoConversionFactors';

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out'
};

const focusedStyle = {
  borderColor: '#2196f3'
};

const acceptStyle = {
  borderColor: '#00e676'
};

const rejectStyle = {
  borderColor: '#ff1744'
};

const defaultChannelMapping = {
  Date: '',
  Pressure: '',
  'Slurry Rate': '',
  'Clean Rate': '',
  'Proppant Conc.': '',
  'BH Proppant Conc.': '',
  'DFR Conc.': '',
  'LFR Conc.': '',
  'Produced Rate': '',
  'TP Rate': '',
  'Cross-Linker Conc.': '',
  'Gellant Conc.': ''
};

const RTFOUploadData = ({ currentRTFO, darkMode, setEditStageVisible }) => {
  const theme = useTheme();
  const currentStageName = currentRTFO?.currentStageName;

  const { socket } = useSocket();
  const { enqueueSnackbar } = useSnackbar();

  const wellNames = useMemo(() => {
    return currentRTFO?.perfSchedule?.wells
      ? Object.keys(currentRTFO.perfSchedule.wells)?.map(w => w.charAt(0).toUpperCase().concat(w.slice(1)))
      : [];
  }, [currentRTFO?.perfSchedule?.wells]);

  const [tabItem, setTabItem] = useState(0);
  const [skipFirstRow, setSkipFirstRow] = useState(true);
  const [autoChannelMapping, setAutoChannelMapping] = useState(true);
  const [delimiterType, setDelimiterType] = useState('auto');
  const [dateFormat] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [fileName, setFileName] = useState(null);
  const [fileSize, setFileSize] = useState(null);
  const [fileUnits, setFileUnits] = useState('metric');
  const [rawData, setRawData] = useState(''); // raw string data from a file
  const [parsedData, setParsedData] = useState([]);
  const [wellName, setWellName] = useState(currentStageName?.split('_')?.[1] || wellNames?.[0] || '');
  const [channelMapping, setChannelMapping] = useState(defaultChannelMapping);

  const units = currentRTFO?.settings?.units || 'metric';

  const stageNumbers = useMemo(() => {
    const maxStageNumber = Math.max(...(Object.keys(currentRTFO.perfSchedule.wells)?.map(wellName => Object.keys(currentRTFO.perfSchedule.wells?.[wellName]?.stages)?.slice(-1)?.[0]))?.flat(Infinity));
    return Array.from({ length: maxStageNumber }, (_, index) => index + 1);
  }, [currentRTFO.perfSchedule.wells]);

  const [stageNumber, setStageNumber] = useState(currentStageName?.split('_')?.[2] || stageNumbers?.slice(-1)?.[0] || '');

  const headers = useMemo(() => {
    return parsedData?.[0] ? Object.keys(parsedData?.[0]) : [];
  }, [parsedData]);

  // update well name when well names change
  useEffect(() => {
    if (wellNames?.length > 0) {
      setWellName(wellNames[0]);
    }
  }, [wellNames]);

  // remap channels when raw data changes
  useEffect(() => {
    let newChannelMapping = defaultChannelMapping;

    if (autoChannelMapping) {
      newChannelMapping = mapChannels(headers, channelMapping);
      setChannelMapping(newChannelMapping);
    } else {
      setChannelMapping(defaultChannelMapping);
    };

    const newParsedData = parseRawStringData(rawData, skipFirstRow, timezone, channelMapping);
    setParsedData(newParsedData);


    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoChannelMapping, rawData, skipFirstRow, timezone]);


  const headerRow = {
    rowId: 'header',
    cells: [{ type: 'header', text: '' }, ...(headers || []).map(h => ({ type: 'header', text: h }))]
  };

  const getColumns = () => [{ columnId: 'index', width: 75 }, ...(headers || []).map(h => ({ columnId: h, width: 150 }))];

  const getRows = data => {
    if (!data?.length > 0) return [];

    const rows = [headerRow];
    data?.forEach((row, i) => {
      const rowCells = [
        { type: 'number', value: parseInt(i) + 1 },
        ...(Object.keys(row) || []).map((d, j) => ({ type: 'text', text: String(row[d]) }))
      ];
      rows.push({ rowId: i, cells: rowCells });
    });
    return rows;
  };

  const rows = getRows(parsedData);
  const columns = getColumns();

  function MyDropzone() {
    const onDrop = useCallback(async acceptedFiles => {
      for (const file of acceptedFiles) {
        const parsedData = await parseStageDataCsv(file, skipFirstRow, setRawData, timezone, channelMapping, units);

        setParsedData(parsedData);
        setFileName(file.path);
        setFileSize(file.size);
      }
    }, []);
    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
      onDrop,
      accept: { 'text/csv': ['.csv'], 'text/txt': ['.txt'] }
    });

    const style = useMemo(
      () => ({
        ...{...baseStyle, backgroundColor: theme.palette.elevationOne[darkMode ? 'dark' : 'light']},
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
      }),
      [isFocused, isDragAccept, isDragReject]
    );

    return (
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
          <CloudUpload color="primary" style={{ fontSize: '64px' }} />
          <Typography color="GrayText" variant="h5">
            Drag and drop or click to upload
          </Typography>
          <Typography color="GrayText" variant="body1">
            Accepts: CSV, TXT, TSV
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <Dialog 
      open={true} 
      onClose={() => setEditStageVisible(false)} 
      fullWidth={true} 
      maxWidth="xl" 
      PaperProps={{
        style: {
          backgroundColor: theme.palette.paper[darkMode ? 'dark' : 'light'],
          boxShadow: 'none'
        }
      }}
      elevation={0}
    >
      <DialogTitle>Edit Stage</DialogTitle>
      <DialogContent>
        <div style={{ width: '100%' }}>
          {/* FILE INFO */}
          {fileName && (
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h4">{fileName || ''}</Typography>
                <Typography variant="h6" color="textSecondary">
                  {formatNumber(fileSize / 1000) || 0} kB
                </Typography>
              </div>
              <IconButton
                onClick={() => {
                  setParsedData([]);
                  setRawData('');
                  setFileName('');
                  setFileSize(null);
                  setChannelMapping(defaultChannelMapping);
                }}
              >
                <Cancel />
              </IconButton>
            </div>
          )}

          {/* UPLOAD CONTAINER*/}
          {!parsedData?.length > 0 && (
            <div style={{ width: '100%' }}>
              <MyDropzone />
            </div>
          )}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
            {/* SETTINGS */}
            <div style={{ width: '50%', marginTop: 20 }}>
              <Tabs
                value={tabItem}
                TabIndicatorProps={{
                  style: {
                    background: darkMode ? '#fff' : theme.palette.primary.main
                  }
                }}
                textColor={darkMode ? 'inherit' : 'primary'}
                onChange={(e, newVal) => setTabItem(newVal)}
                style={{
                  // backgroundColor: darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light,
                  borderBottom: `1px solid ${darkMode ? 'dimgrey' : 'lightgrey'}`
                }}
              >
                <Tab label="Settings" />
                <Tab label="Channel Mapping" />
              </Tabs>

              <div style={{ height: '700px', overflow: 'auto', padding: 20 }}>
                {tabItem === 0 ? (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                      <Typography style={{ width: '30%' }}>Well name</Typography>
                      <FormControl>
                        {/* <FormLabel>Well Name</FormLabel> */}

                        <Select
                          value={wellName}
                          onChange={e => setWellName(e.target.value)}
                          size="small"
                          variant="outlined"
                          style={{ width: 300 }}
                        >
                          {wellNames?.map((item, i) => {
                            return (
                              <MenuItem key={`${item}-${i}`} value={item}>
                                {`${item} ${currentRTFO?.wellAlias?.[item] ? `(${currentRTFO?.wellAlias?.[item]})` : ''}`}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </div>

                    <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: 20 }}>
                      <Typography style={{ width: '30%' }}>Stage number</Typography>

                      <FormControl>
                        {/* <FormLabel>Stage Number</FormLabel> */}
                        <Select
                          value={stageNumber}
                          onChange={e => setStageNumber(e.target.value)}
                          size="small"
                          variant="outlined"
                          style={{ width: 300 }}
                        >
                          {stageNumbers?.map((item, i) => {
                            return (
                              <MenuItem key={`${item}-${i}`} value={item}>
                                {item}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </div>

                    <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: 20 }}>
                      <Typography style={{ width: '30%' }}>Well UWI</Typography>
                      <TextField value={currentRTFO?.wellMapping?.[wellName?.toLowerCase()]} style={{ width: '300px' }} disabled />
                    </div>

                    <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: 20 }}>
                      <Typography style={{ width: '30%' }}>Stage name</Typography>
                      <TextField value={`${currentRTFO?.name}_${wellName}_${stageNumber}`} style={{ width: '300px' }} disabled />
                    </div>

                    <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: 20 }}>
                      <Typography style={{ width: '30%' }}>Delimiter</Typography>
                      <FormControl disabled>
                        {/* <FormLabel>Delimiter</FormLabel> */}
                        <RadioGroup row value={delimiterType} onChange={(e, newVal) => setDelimiterType(newVal)}>
                          <FormControlLabel value="auto" control={<Radio />} label="Auto" />
                          <FormControlLabel value="comma" control={<Radio />} label="Comma" />
                          <FormControlLabel value="tab" control={<Radio />} label="Tab" />
                          <FormControlLabel value="space" control={<Radio />} label="Space" />
                          <FormControlLabel value="semicolon" control={<Radio />} label="Semicolon" />
                        </RadioGroup>
                      </FormControl>
                    </div>

                    <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: 20 }}>
                      <Typography style={{ width: '30%' }}>Units</Typography>
                      <FormControl disabled>
                        <RadioGroup row value={fileUnits} onChange={(e, newVal) => setFileUnits(newVal)}>
                          <FormControlLabel value="metric" control={<Radio />} label="Metric" />
                          <FormControlLabel value="field" control={<Radio />} label="Field" />
                        </RadioGroup>
                      </FormControl>
                    </div>

                    <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: 20 }}>
                      <Typography style={{ width: '30%' }}>Automatic channel mapping</Typography>
                      <Switch checked={autoChannelMapping} onChange={e => setAutoChannelMapping(e.target.checked)} />
                    </div>

                    <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: 20 }}>
                      <Typography style={{ width: '30%' }}>Skip first row</Typography>
                      <Switch checked={skipFirstRow} onChange={e => setSkipFirstRow(e.target.checked)} />
                    </div>

                    <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: 20 }}>
                      <Typography style={{ width: '30%' }}>Date format</Typography>
                      <TextField value={dateFormat} placeholder="MM/DD/YYYY HH:MM:SS" style={{ width: 300 }} />
                    </div>

                    <div style={{ display: 'flex', width: '100%', alignItems: 'center', marginTop: 20 }}>
                      <Typography style={{ width: '30%' }}>Time zone</Typography>

                      <FormControl>
                        <Select
                          value={timezone}
                          onChange={e => setTimezone(e.target.value)}
                          size="small"
                          variant="outlined"
                          style={{ width: 300 }}
                        >
                          {['UTC', 'MDT', 'MST']?.map((item, i) => {
                            return (
                              <MenuItem key={`${item}-${i}`} value={item}>
                                {item} {item === 'UTC' ? '(UTC+00:00)' : item === 'MST' ? '(UTC-07:00)' : '(UTC-06:00)'}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                ) : (
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    {[
                      'Date',
                      'Pressure',
                      'Slurry Rate',
                      'Clean Rate',
                      'Produced Rate',
                      'TP Rate',
                      'Proppant Conc.',
                      'BH Proppant Conc.',
                      'DFR Conc.',
                      'LFR Conc.',
                      'Cross-Linker Conc.',
                      'Gellant Conc.'
                    ].map(channel => {
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column' }} key={channel}>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              margin: '10px 0'
                            }}
                          >
                            <Typography style={{ width: '30%' }}>{channel}</Typography>

                            <Autocomplete
                              {...{ options: ['', ...headers] }}
                              value={channelMapping[channel]}
                              onChange={(event, newValue) => {
                                setChannelMapping(prev => ({ ...prev, [channel]: newValue }));
                              }}
                              renderInput={params => <TextField {...params} label={`${channel} Channel`} variant="outlined" />}
                              style={{ width: 300 }}
                            />
                          </div>
                          <Divider />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* CHART */}
            <div style={{ width: '50%', height: 600, marginTop: 50 }}>
              <RTFOUploadDataChart parsedData={parsedData} channelMapping={channelMapping} darkMode={darkMode} units={units} />
            </div>
          </div>

          {/* DATA TABLE */}
          {parsedData?.length > 0 && (
            <div style={{ width: '100%', height: '500px', overflow: 'scroll', marginTop: 20 }}>
              <ReactGrid
                divProps={{
                  style: {
                    width: "auto"
                  }
                }}
                rows={rows}
                columns={columns}
                enableRangeSelection
                enableRowSelection
                enableFillHandle
                stickyTopRows={1}
                stickyLeftColumns={1}
              />
            </div>
          )}

          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: 40 }}>
            <Button 
              color={darkMode ? "inherit" : "secondary"} onClick={() => setEditStageVisible(false)} style={{ marginRight: 20 }}>
              Cancel
            </Button>
            <Button color={darkMode ? "inherit" : "secondary"} variant="contained" onClick={handleCalculateClick} disabled={parsedData?.length === 0}>
              Calculate
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  function handleCalculateClick() {
    if (socket?.readyState !== 1) return;

    const stageName = `${currentRTFO?.name}_${wellName}_${stageNumber}`;
    enqueueSnackbar(`Processing stage ${stageName}`, { variant: 'info' });

    setEditStageVisible(false);

    // compile pumping data
    const pumpingData = [];

    for (let i = 0; i < parsedData.length; i++) {
      const d = parsedData[i];
      const cleanVolume = (i === 0 ? d.time * d.clean : (d.time - pumpingData[i - 1]?.time) * d.clean) || 0;
      const totalCleanVolume = i === 0 ? cleanVolume : pumpingData[i - 1]?.cleanVolume + cleanVolume;
      const slurryVolume = i === 0 ? d.time * d.rate : (d.time - pumpingData[i - 1]?.time) * d.rate;
      const totalSlurryVolume = i === 0 ? slurryVolume : pumpingData[i - 1]?.slurry + slurryVolume;

      let date = new Date(d[channelMapping['Date']]);

      if (!date) continue;

      pumpingData.push({
        date: date?.toISOString(),
        timestamp: date?.getTime(),
        pressure: (+d[channelMapping['Pressure']] || 0) / (units === 'field' ? rtfoConversionFactors.pressure : 1),
        rate: (+d[channelMapping['Slurry Rate']] || 0) / (units === 'field' ? rtfoConversionFactors.rate : 1),
        clean: (+d[channelMapping['Clean Rate']] || 0) / (units === 'field' ? rtfoConversionFactors.rate : 1),
        slurryVolume: totalSlurryVolume / (units === 'field' ? rtfoConversionFactors.slurryVolume : 1),
        cleanVolume: totalCleanVolume / (units === 'field' ? rtfoConversionFactors.cleanVolume : 1),
        proppant: (+d[channelMapping['Proppant Conc.']] || 0) / (units === 'field' ? rtfoConversionFactors.proppant : 1),
        proppantBH: (+d[channelMapping['BH Proppant Conc.']] || 0) / (units === 'field' ? rtfoConversionFactors.proppant : 1),
        fr: (+d[channelMapping['DFR Conc.']] || 0) / (units === 'field' ? rtfoConversionFactors.fr : 1),
        fr2: (+d[channelMapping['LFR Conc.']] || 0) / (units === 'field' ? rtfoConversionFactors.fr2 : 1),
        producedRate: (+d[channelMapping['Produced Rate']] || 0) / (units === 'field' ? rtfoConversionFactors.rate : 1),
        tpRate: (+d[channelMapping['TP Rate']] || 0) / (units === 'field' ? rtfoConversionFactors.tpRate : 1),
        crossLinker: (+d[channelMapping['Cross-Linker Conc.']] || 0) / (units === 'field' ? rtfoConversionFactors.crossLinker : 1),
        gellant: (+d[channelMapping['Gellant']] || 0) / (units === 'field' ? rtfoConversionFactors.gellant : 1)
      });
    };

    // calculate the time lapsed since start for each point in minutes
    const timeStep = (pumpingData[1]?.timestamp - pumpingData[0]?.timestamp) / 60000; // time step in minutes
    pumpingData.forEach((d, i) => {
      pumpingData[i].time = i === 0 ? timeStep : (d.timestamp - pumpingData[0]?.timestamp) / 60000 + timeStep;
    });

    // CALCULATE CUMULATIVE SLURRY VOLUME AND CLEAN VOLUME
    pumpingData.forEach((d, i) => {
      const cleanVolume = (i === 0 ? d.time * d.clean : (d.time - pumpingData[i - 1]?.time) * d.clean) || 0;
      const totalCleanVolume = i === 0 ? cleanVolume : pumpingData[i - 1]?.cleanVolume + cleanVolume;
      const slurryVolume = i === 0 ? d.time * d.rate : (d.time - pumpingData[i - 1]?.time) * d.rate;
      const totalSlurryVolume = i === 0 ? slurryVolume : pumpingData[i - 1]?.slurry + slurryVolume;
      pumpingData[i].cleanVolume = totalCleanVolume;
      pumpingData[i].slurry = totalSlurryVolume;
    });

    socket.send(JSON.stringify({
      messageType: 'recalculateStageCSV',
      padName: currentRTFO?.name,
      stageName,
      pumpingData
    }))
  }
};

export async function parseStageDataCsv(file, skipFirstRow, setRawData, timezone, channelMapping, units) {
  try {
    if (!file) return false;

    return new Promise((resolve, reject) => {
      let reader = new FileReader();

      reader.readAsText(file);

      let parsedData = [];

      reader.onload = event => {
        let result = event.target.result;

        if (result.length > 0) {
          parsedData = parseRawStringData(result, skipFirstRow, timezone, channelMapping, units);

          parsedData.sort((a, b) => a.timestamp - b.timestamp);
          setRawData(result);
          resolve(parsedData);
        };
      };

      reader.onerror = () => {
        reject({ rawData: null, fileName: null, fileSize: null });
      };
    });
  } catch (error) {
    console.log(error);
    return { rawData: null, fileName: null, fileSize: null };
  }
}

function parseRawStringData(rawStringData, skipFirstRow, timezone, channelMapping, units) {
  try {
    const parsedData = [];

    if (rawStringData.length > 0) {
      let data = rawStringData.split('\r\n');
      const headers = data?.splice(0, 1)?.[0]?.split(','); // get csv headers

      if (skipFirstRow) {
        data.splice(0, 1);
      };

      data.forEach(d => {
        if (d.replaceAll(/,/g, '')?.length > 0) {
          const row = d?.split(',');

          const rowValue = {};
          let dateUpdated = false;

          headers.forEach((h, i) => {

            if (h === channelMapping?.['Date'] && timezone !== 'UTC' && !dateUpdated) {
              let date = new Date(new Date(row[i])?.getTime() + (timezone === 'MST' ? 7 * 60 * 60 * 1000 : timezone === 'MDT' ? 6 * 60 * 60 * 1000 : 0))?.toLocaleString();
              rowValue[h] = date;
              dateUpdated = true;
            } else {
              if (Object.prototype.hasOwnProperty.call(rowValue, h)) {
                rowValue[`${h}${i}`] = parseValue(row[i]);
              } else {
                rowValue[h] = parseValue(row[i]);
              };
            };

          });

          parsedData.push(rowValue);
        };
      });


      // convert units from field to metric
      if (units === 'field') {
        parsedData.forEach((d, i) => {
          parsedData[i].pressure = d.pressure * (units === 'field' ? rtfoConversionFactors.pressure : 1);
          parsedData[i].rate = d.rate * (units === 'field' ? rtfoConversionFactors.rate : 1);
          parsedData[i].rate = d.rate * (units === 'field' ? rtfoConversionFactors.rate : 1);
        })
      }
    };
    return parsedData;
  } catch (error) {
    console.log(error);
  };

  function parseValue(value) {
    const regex = /\/.+\//g;
    if (regex.test(value)) {
      return value;
    } else {
      return isNaN(parseFloat(value)) ? value : parseFloat(value);
    };
  };
};

function mapChannels(headers, channelMapping) {
  try {
    if (!headers || !channelMapping) return {};
    const newChannelMapping = JSON.parse(JSON.stringify(channelMapping));

    for (const channelName of Object.keys(channelMapping)) {
      const regex = getMatchingRegex(channelName);

      for (const header of headers) {
        if (regex && regex.test(header)) {
          newChannelMapping[channelName] = header;
        };
      };
    };

    return newChannelMapping;
  } catch (error) {
    console.log(error);
    return channelMapping;
  };

  function getMatchingRegex(channelName) {
    switch (channelName) {
      case 'Date': {
        return /job time$|job_time/gi;
      }
      case 'Pressure': {
        return /treating pressure|treating_pressure/gi;
      }
      case 'Slurry Rate': {
        return /slurry rate|slurry_rate/gi;
      }
      case 'Clean Rate': {
        return /clean rate|clean_rate/gi;
      }
      case 'Produced Rate': {
        return /produced rate/gi;
      }
      case 'TP Rate': {
        return /tp rate/gi;
      }
      case 'Proppant Conc.': {
        return /^prop conc|wellhead_prop_conc|blender_conc/gi;
      }
      case 'BH Proppant Conc.': {
        return /bh prop conc|btmh_prop_conc/gi;
      }
      case 'DFR Conc.': {
        return /dfr-b665|b665 conc/gi;
      }
      case 'LFR Conc.': {
        return /frp-b620|frpb620 conc/gi;
      }
      default:
        return null;
    };
  };
};

export default RTFOUploadData;
